import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import Layout from '../../Components/Layout';
import config from '../../config';
import Swal from 'sweetalert2';
import { FaTrash, FaShoppingCart, FaInfoCircle, FaClock, FaMapMarkerAlt, FaUsers, FaCalendarAlt } from 'react-icons/fa';
import { useCurrency } from '../../context/CurrencyContext';
import { Modal } from 'react-bootstrap';
import { TiTick } from "react-icons/ti";
import { ImCross } from "react-icons/im";

export default function Cart() {
    const navigate = useNavigate();
    const location = useLocation();
    const [cart, setCart] = useState([]);
    const [loading, setLoading] = useState(true);
    const [removeLoading, setRemoveLoading] = useState(false);
    const [totalPrice, setTotalPrice] = useState(0);
    const [selectedItem, setSelectedItem] = useState(null);
    const { selectedCurrency } = useCurrency();
    const userData = JSON.parse(localStorage.getItem('user_data'));
    const [convertedPrices, setConvertedPrices] = useState({});
    const [cancellationPrices, setCancellationPrices] = useState({});
    
    // Auto clear cart timer states
    const [timeLeft, setTimeLeft] = useState(0);
    const [isTimerActive, setIsTimerActive] = useState(false);

    // Auto clear cart functionality
    const AUTO_CLEAR_TIME = 10 * 60; // 10 minutes in seconds
    const TIMER_KEY = 'cart_auto_clear_timer';
    const TIMER_START_KEY = 'cart_timer_start';

    const initializeTimer = () => {
        const savedStartTime = localStorage.getItem(TIMER_START_KEY);
        const now = Date.now();
        
        if (savedStartTime) {
            const elapsed = Math.floor((now - parseInt(savedStartTime)) / 1000);
            const remaining = AUTO_CLEAR_TIME - elapsed;
            
            if (remaining > 0) {
                setTimeLeft(remaining);
                setIsTimerActive(true);
            } else {
                // Timer expired, clear cart
                handleAutoClearCart();
            }
        } else {
            // Start new timer
            localStorage.setItem(TIMER_START_KEY, now.toString());
            setTimeLeft(AUTO_CLEAR_TIME);
            setIsTimerActive(true);
        }
    };

    const resetTimer = () => {
        localStorage.removeItem(TIMER_START_KEY);
        setTimeLeft(0);
        setIsTimerActive(false);
    };

    const handleAutoClearCart = async () => {
        try {
            if (!userData?.token) return;

            const response = await axios.delete(`${config.API_BASE_URL_V1}basket/delete`, {
                headers: {
                    Authorization: `Bearer ${userData.token}`
                }
            });

            if (response.data.status === 'success') {
                setCart([]);
                setTotalPrice(0);
                resetTimer();
                
                Swal.fire({
                toast: true,
                    position: 'top-right',
                    icon: 'success',
                    title: 'Cart Cleared',
                    text: 'Cart has been cleared due to inactivity.',
                    showConfirmButton: false,
                    timer: 1500,
                    timerProgressBar: true
                });
            }
        } catch (error) {
            console.error('Error auto-clearing cart:', error);
            resetTimer();
        }
    };

    const formatTime = (seconds) => {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    };

    // Timer effect
    useEffect(() => {
        let interval;
        
        if (isTimerActive && timeLeft > 0) {
            interval = setInterval(() => {
                setTimeLeft(prev => {
                    if (prev <= 1) {
                        handleAutoClearCart();
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        }

        return () => {
            if (interval) clearInterval(interval);
        };
    }, [isTimerActive, timeLeft]);

    useEffect(() => {
        const fetchCart = async () => {
            try {
                if (!userData?.token) {
                    Swal.fire({
                        title: 'Please Login',
                        text: 'You need to login first to view your cart',
                        icon: 'warning',
                        confirmButtonText: 'OK'
                    });
                    navigate('/login');
                    return;
                }

                const response = await axios.get(`${config.API_BASE_URL_V1}basket`, {
                    headers: {
                        Authorization: `Bearer ${userData.token}`
                    }
                });

                if (response.data.status === 'success' && response.data.data?.basket?.basketItems) {
                    const basketItems = response.data.data.basket.basketItems;
                    setCart(basketItems);
                    setTotalPrice(response.data.data.basket.total_price);
                    
                    // Initialize timer only if cart has items
                    if (basketItems.length > 0) {
                        initializeTimer();
                    } else {
                        resetTimer();
                    }
                } else {
                    throw new Error(response.data.message || 'Failed to fetch cart');
                }
            } catch (error) {
                console.error('Error fetching cart:', error);
                setCart([]);
                setTotalPrice(0);
                resetTimer();
            } finally {
                setLoading(false);
            }
        };

        fetchCart();
    }, [userData?.token, navigate]);

    // Cleanup timer on component unmount
    useEffect(() => {
        return () => {
            // Don't clear timer on unmount, let it continue in background
        };
    }, []);

    const handleRemoveItem = async (basketItemId) => {
        try {
            setRemoveLoading(true);
            const result = await Swal.fire({
                title: 'Are you sure?',
                text: "Do you want to remove this item from your cart?",
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: 'Yes, remove it!',
                cancelButtonText: 'Cancel'
            });

            if (result.isConfirmed) {
                const response = await axios.delete(`${config.API_BASE_URL_V1}basket/delete/item/${basketItemId}`, {
                    headers: {
                        Authorization: `Bearer ${userData.token}`
                    }
                });

                if (response.data.status === 'success') {
                    // Update cart
                    const basketResponse = await axios.get(`${config.API_BASE_URL_V1}basket`, {
                        headers: {
                            Authorization: `Bearer ${userData.token}`
                        }
                    });

                    if (basketResponse.data.status === 'success' && basketResponse.data.data?.basket?.basketItems) {
                        const basketItems = basketResponse.data.data.basket.basketItems;
                        setCart(basketItems);
                        setTotalPrice(basketResponse.data.data.basket.total_price);
                        
                        // Reset timer if cart becomes empty, otherwise restart timer
                        if (basketItems.length === 0) {
                            resetTimer();
                        } else {
                            // Restart timer for remaining items
                            localStorage.setItem(TIMER_START_KEY, Date.now().toString());
                            setTimeLeft(AUTO_CLEAR_TIME);
                            setIsTimerActive(true);
                        }
                    }

                    Swal.fire({
                        title: 'Success!',
                        text: 'Item has been removed from your cart.',
                        icon: 'success',
                        confirmButtonText: 'OK'
                    }).then(() => {
                        window.location.reload();
                    });
                } else {
                    throw new Error(response.data.message || 'Failed to remove item');
                }
            }
        } catch (error) {
            console.error('Error removing item:', error);
            Swal.fire({
                title: 'Error!',
                text: error.response?.data?.message || 'An error occurred while removing the item',
                icon: 'error',
                confirmButtonText: 'OK'
            });
        } finally {
            setRemoveLoading(false);
        }
    };

    const handleProceedToCheckout = async () => {
        try {
            if (!userData?.token) {
                Swal.fire({
                    title: 'Please Login',
                    text: 'You need to login first to proceed to checkout',
                    icon: 'warning',
                    confirmButtonText: 'OK'
                });
                navigate('/login');
                return;
            }

            // Reset timer when proceeding to checkout
            resetTimer();
            navigate('/checkout');
        } catch (error) {
            console.error('Error proceeding to checkout:', error);
            Swal.fire({
                title: 'Error!',
                text: error.response?.data?.message || 'Failed to proceed to checkout',
                icon: 'error',
                confirmButtonText: 'OK'
            });
        }
    };

    const handleClearCart = async () => {
        try {
            const result = await Swal.fire({
                title: 'Clear Cart',
                text: 'Are you sure you want to clear your cart?',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: 'Yes, clear it!',
                cancelButtonText: 'Cancel'
            });

            if (result.isConfirmed) {
                const response = await axios.delete(`${config.API_BASE_URL_V1}basket/delete`, {
                    headers: {
                        Authorization: `Bearer ${userData.token}`
                    }
                });

                if (response.data.status === 'success') {
                    setCart([]);
                    setTotalPrice(0);
                    resetTimer(); // Reset timer when cart is manually cleared
                    Swal.fire({
                        toast: true,
                        position: 'top-right',
                        icon: 'success',
                        title: 'Cleared!',
                        text: 'Your cart has been cleared.',
                        showConfirmButton: false,
                        timer: 1500
                    });
                } else {
                    throw new Error(response.data.message || 'Failed to clear cart');
                }
            }
        } catch (error) {
            console.error('Error clearing cart:', error);
            Swal.fire({
                title: 'Error!',
                text: error.response?.data?.message || 'Failed to clear cart',
                icon: 'error',
                confirmButtonText: 'OK'
            });
        }
    };

    const handleContinueShopping = () => {
        const from = location.state?.from || '/hotels';
        navigate(from);
    };

    const formatCancellationDate = (dateString) => {
        const date = new Date(dateString);
        const hours = date.getHours();
        const minutes = date.getMinutes();
        
        const dateOptions = {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        };

        if (hours === 3 && minutes === 0) {
            return `${new Intl.DateTimeFormat('en-US', dateOptions).format(date)} at 23:59`;
        } else if (hours === 0 && minutes === 0) {
            return new Intl.DateTimeFormat('en-US', dateOptions).format(date);
        } else {
            return `${new Intl.DateTimeFormat('en-US', dateOptions).format(date)} at ${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
        }
    };

    const formatDateRange = (checkIn, checkOut) => {
        const startDate = new Date(checkIn);
        const endDate = new Date(checkOut);
        
        const startFormatted = startDate.toLocaleDateString('en-US', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
        });
        
        const endFormatted = endDate.toLocaleDateString('en-US', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
        });
        
        return `${startFormatted} - ${endFormatted}`;
    };

    // Render Tour Item
    const renderTourItem = (item, index) => (
        <div key={index} className="card cart-item" style={{
            borderRadius: '15px',
            border: 'none',
            boxShadow: '0 0 20px rgba(0,0,0,0.05)',
            marginBottom: '20px',
            transition: 'transform 0.3s ease',
            position: 'relative'
        }}>
            <button 
                onClick={() => handleRemoveItem(item.id)}
                className="remove-item-btn"
                style={{
                    position: 'absolute',
                    top: '15px',
                    right: '15px',
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    background: 'rgba(220, 53, 69, 0.1)',
                    border: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    zIndex: 1,
                    opacity: removeLoading ? 0.5 : 1
                }}
                disabled={removeLoading}
            >
                {removeLoading ? (
                    <div className="spinner-border spinner-border-sm text-danger" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                ) : (
                    <FaTrash style={{ 
                        color: '#dc3545',
                        fontSize: '14px',
                        transition: 'all 0.3s ease'
                    }} />
                )}
            </button>
            <div className="card-body">
                <div className="row">
                    <div className="col-md-4">
                        <div className="cart-item-image" style={{
                            height: '200px',
                            overflow: 'hidden',
                            borderRadius: '10px',
                            marginBottom: '10px'
                        }}>
                            <img 
                                src={item.tour.thumbnail || "https://via.placeholder.com/400x200"} 
                                alt={item.tour.name} 
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover'
                                }}
                                onContextMenu={(e) => e.preventDefault()}
                                onDragStart={(e) => e.preventDefault()}
                            />
                        </div>
                        {/* Tour Type Badge */}
                        <div style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            padding: '6px 12px',
                            background: 'linear-gradient(135deg, rgba(255, 140, 70, 0.15) 0%, rgba(220, 110, 50, 0.1) 100%)',
                            border: '1px solid rgba(255, 180, 120, 0.4)',
                            borderRadius: '20px',
                            fontSize: '0.75rem',
                            fontWeight: '600',
                            color: 'rgba(210, 110, 50, 0.9)',
                            marginBottom: '10px'
                        }}>
                            <FaMapMarkerAlt style={{ fontSize: '12px' }} />
                            <span>TOUR</span>
                        </div>
                    </div>
                    <div className="col-md-8">
                        <div className="cart-item-details">
                            <h5 style={{ 
                                color: 'rgba(210, 110, 50, 0.95)',
                                fontSize: '1.5rem',
                                fontWeight: '600',
                                marginBottom: '10px'
                            }}>{item.tour.name}</h5>
                            
                            {/* Tour Details */}
                            <div className="info-group" style={{
                                background: 'linear-gradient(135deg, rgba(255, 245, 235, 0.7) 0%, rgba(255, 235, 215, 0.5) 100%)',
                                padding: '12px',
                                borderRadius: '8px',
                                marginBottom: '10px',
                                border: '1px solid rgba(255, 200, 150, 0.3)'
                            }}>
                                <div className="d-flex justify-content-between align-items-center">
                                    <div className="d-flex align-items-center gap-3">
                                        <div className="d-flex align-items-center gap-1">
                                            <FaMapMarkerAlt style={{ color: 'rgba(220, 120, 60, 0.8)', fontSize: '14px' }} />
                                            <span style={{ 
                                                color: 'rgba(210, 110, 50, 0.9)',
                                                fontSize: '0.9rem',
                                                fontWeight: '500'
                                            }}>Area:</span>
                                            <span style={{ color: '#666' }}>{item.tour.area}</span>
                                        </div>
                                    </div>
                                    <div className="d-flex align-items-center gap-1">
                                        <FaCalendarAlt style={{ color: 'rgba(220, 120, 60, 0.8)', fontSize: '14px' }} />
                                        <span style={{ 
                                            color: 'rgba(210, 110, 50, 0.9)',
                                            fontSize: '0.9rem',
                                            fontWeight: '500'
                                        }}>Duration:</span>
                                        <span style={{ color: '#666' }}>{item.tour.duration}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Guests Info */}
                            <div className="info-group" style={{
                                background: 'linear-gradient(135deg, rgba(255, 245, 235, 0.7) 0%, rgba(255, 235, 215, 0.5) 100%)',
                                padding: '12px',
                                borderRadius: '8px',
                                marginBottom: '10px',
                                border: '1px solid rgba(255, 200, 150, 0.3)'
                            }}>
                                <div className="d-flex justify-content-between align-items-center">
                                    <div className="d-flex align-items-center gap-1">
                                        <FaUsers style={{ color: 'rgba(220, 120, 60, 0.8)', fontSize: '14px' }} />
                                        <span style={{ 
                                            color: 'rgba(210, 110, 50, 0.9)',
                                            fontSize: '0.9rem',
                                            fontWeight: '500'
                                        }}>Guests:</span>
                                        <span style={{ color: '#666' }}>
                                            {item.booking_details.adults} Adult{item.booking_details.adults > 1 ? 's' : ''}
                                            {item.booking_details.children > 0 ? `, ${item.booking_details.children} Child${item.booking_details.children > 1 ? 'ren' : ''}` : ''}
                                        </span>
                                    </div>
                                    <div>
                                        <span style={{ 
                                            color: 'rgba(210, 110, 50, 0.9)',
                                            fontSize: '0.9rem',
                                            fontWeight: '500'
                                        }}>Days/Nights:</span>
                                        <span style={{ color: '#666', marginLeft: '5px' }}>{item.tour.daysNights}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Tour Dates */}
                            <div className="info-group" style={{
                                background: 'linear-gradient(135deg, rgba(255, 245, 235, 0.7) 0%, rgba(255, 235, 215, 0.5) 100%)',
                                padding: '12px',
                                borderRadius: '8px',
                                marginBottom: '10px',
                                border: '1px solid rgba(255, 200, 150, 0.3)'
                            }}>
                                <div className="d-flex justify-content-between align-items-center">
                                    <div className="d-flex align-items-center" style={{ gap: '20px' }}>
                                        <div>
                                            <span style={{ color: '#757575', fontSize: '0.9rem' }}>Tour Date</span>
                                            <div style={{ color: '#212121', fontWeight: '500' }}>
                                                {formatDateRange(item.check_in, item.check_out)}
                                            </div>
                                        </div>
                                        <div>
                                            <span style={{ color: '#757575', fontSize: '0.9rem' }}>Total Days</span>
                                            <div style={{ color: '#212121', fontWeight: '500' }}>
                                                {item.days} Day{item.days > 1 ? 's' : ''}
                                            </div>
                                        </div>
                                    </div>
                                    <div>
                                        <span style={{ color: '#757575', fontSize: '0.9rem' }}>Price</span>
                                        <div style={{ color: 'rgba(210, 110, 50, 0.95)', fontSize: '1.2rem', fontWeight: '600' }}>
                                            {item.price} {selectedCurrency}
                                            <small style={{ color: '#666', fontSize: '0.8rem', marginLeft: '5px' }}>
                                                ({item.quantity} tour{item.quantity === 1 ? '' : 's'})
                                            </small>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    // Render Transfer Item
    const renderTransferItem = (item, index) => (
        <div key={index} className="card cart-item" style={{
            borderRadius: '15px',
            border: 'none',
            boxShadow: '0 0 20px rgba(0,0,0,0.05)',
            marginBottom: '20px',
            transition: 'transform 0.3s ease',
            position: 'relative'
        }}>
            <button 
                onClick={() => handleRemoveItem(item.id)}
                className="remove-item-btn"
                style={{
                    position: 'absolute',
                    top: '15px',
                    right: '15px',
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    background: 'rgba(220, 53, 69, 0.1)',
                    border: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    zIndex: 1,
                    opacity: removeLoading ? 0.5 : 1
                }}
                disabled={removeLoading}
            >
                {removeLoading ? (
                    <div className="spinner-border spinner-border-sm text-danger" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                ) : (
                    <FaTrash style={{ 
                        color: '#dc3545',
                        fontSize: '14px',
                        transition: 'all 0.3s ease'
                    }} />
                )}
            </button>
            <div className="card-body">
                <div className="row">
                    <div className="col-md-4">
                        <div className="cart-item-image" style={{
                            height: '200px',
                            overflow: 'hidden',
                            borderRadius: '10px',
                            marginBottom: '10px',
                            background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: '2px dashed #dee2e6'
                        }}>
                            <div className="text-center">
                                <i className="isax isax-car" style={{ 
                                    fontSize: '48px', 
                                    color: 'rgba(220, 120, 60, 0.8)',
                                    marginBottom: '10px'
                                }}></i>
                                <div style={{ 
                                    color: 'rgba(210, 110, 50, 0.9)',
                                    fontSize: '0.9rem',
                                    fontWeight: '600'
                                }}>
                                    {item.transfer.vehicle.type} {item.transfer.vehicle.category}
                                </div>
                            </div>
                        </div>
                        {/* Transfer Type Badge */}
                        <div style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            padding: '6px 12px',
                            background: 'linear-gradient(135deg, rgba(255, 140, 70, 0.15) 0%, rgba(220, 110, 50, 0.1) 100%)',
                            border: '1px solid rgba(255, 180, 120, 0.4)',
                            borderRadius: '20px',
                            fontSize: '0.75rem',
                            fontWeight: '600',
                            color: 'rgba(210, 110, 50, 0.9)',
                            marginBottom: '10px'
                        }}>
                            <i className="isax isax-car" style={{ fontSize: '12px' }}></i>
                            <span>TRANSFER</span>
                        </div>
                    </div>
                    <div className="col-md-8">
                        <div className="cart-item-details">
                            <h5 style={{ 
                                color: 'rgba(210, 110, 50, 0.95)',
                                fontSize: '1.5rem',
                                fontWeight: '600',
                                marginBottom: '10px'
                            }}>
                                {item.transfer.vehicle.type} {item.transfer.vehicle.category} Transfer
                            </h5>
                            
                            {/* Route Details */}
                            <div className="info-group" style={{
                                background: 'linear-gradient(135deg, rgba(255, 245, 235, 0.7) 0%, rgba(255, 235, 215, 0.5) 100%)',
                                padding: '12px',
                                borderRadius: '8px',
                                marginBottom: '10px',
                                border: '1px solid rgba(255, 200, 150, 0.3)'
                            }}>
                                <div className="d-flex justify-content-between align-items-center">
                                    <div className="d-flex align-items-center gap-3">
                                        <div className="d-flex align-items-center gap-1">
                                            <FaMapMarkerAlt style={{ color: 'rgba(220, 120, 60, 0.8)', fontSize: '14px' }} />
                                            <span style={{ 
                                                color: 'rgba(210, 110, 50, 0.9)',
                                                fontSize: '0.9rem',
                                                fontWeight: '500'
                                            }}>Route:</span>
                                            <span style={{ color: '#666' }}>
                                                {item.transfer.routes && item.transfer.routes.length > 1 ? (
                                                    // Multiple routes
                                                    item.transfer.routes.map((route, index) => (
                                                        <div key={index} style={{ fontSize: '0.85rem' }}>
                                                            {route.from.name} → {route.to.name} 
                                                            <small style={{ color: '#999', marginLeft: '5px' }}>
                                                                ({route.type === 'departure' ? 'Departure' : 'Return'})
                                                            </small>
                                                        </div>
                                                    ))
                                                ) : (
                                                    // Single route
                                                    `${item.transfer.route.from.name} → ${item.transfer.route.to.name}`
                                                )}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="d-flex align-items-center gap-1">
                                        <i className="isax isax-car" style={{ color: 'rgba(220, 120, 60, 0.8)', fontSize: '14px' }}></i>
                                        <span style={{ 
                                            color: 'rgba(210, 110, 50, 0.9)',
                                            fontSize: '0.9rem',
                                            fontWeight: '500'
                                        }}>Vehicle:</span>
                                        <span style={{ color: '#666' }}>{item.transfer.vehicle.code}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Vehicle & Passengers Info */}
                            <div className="info-group" style={{
                                background: 'linear-gradient(135deg, rgba(255, 245, 235, 0.7) 0%, rgba(255, 235, 215, 0.5) 100%)',
                                padding: '12px',
                                borderRadius: '8px',
                                marginBottom: '10px',
                                border: '1px solid rgba(255, 200, 150, 0.3)'
                            }}>
                                <div className="d-flex justify-content-between align-items-center">
                                    <div className="d-flex align-items-center gap-1">
                                        <FaUsers style={{ color: 'rgba(220, 120, 60, 0.8)', fontSize: '14px' }} />
                                        <span style={{ 
                                            color: 'rgba(210, 110, 50, 0.9)',
                                            fontSize: '0.9rem',
                                            fontWeight: '500'
                                        }}>Passengers:</span>
                                        <span style={{ color: '#666' }}>
                                            {item.passengers.adults} Adult{item.passengers.adults > 1 ? 's' : ''}
                                            {item.passengers.children > 0 ? `, ${item.passengers.children} Child${item.passengers.children > 1 ? 'ren' : ''}` : ''}
                                            {' '}({item.passengers.total} total)
                                        </span>
                                    </div>
                                    <div>
                                        <span style={{ 
                                            color: 'rgba(210, 110, 50, 0.9)',
                                            fontSize: '0.9rem',
                                            fontWeight: '500'
                                        }}>Capacity:</span>
                                        <span style={{ color: '#666', marginLeft: '5px' }}>
                                            {item.transfer.vehicle.max_passengers} passengers, {item.transfer.vehicle.baggage_capacity} bags
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Transfer Date & Flight Info */}
                            <div className="info-group" style={{
                                background: 'linear-gradient(135deg, rgba(255, 245, 235, 0.7) 0%, rgba(255, 235, 215, 0.5) 100%)',
                                padding: '12px',
                                borderRadius: '8px',
                                marginBottom: '10px',
                                border: '1px solid rgba(255, 200, 150, 0.3)'
                            }}>
                                <div className="d-flex justify-content-between align-items-center">
                                    <div className="d-flex align-items-center" style={{ gap: '20px' }}>
                                        <div>
                                            <span style={{ color: '#757575', fontSize: '0.9rem' }}>Transfer Date</span>
                                            <div style={{ color: '#212121', fontWeight: '500' }}>
                                                {new Date(item.departure_date).toLocaleDateString('en-US', {
                                                    day: 'numeric',
                                                    month: 'short',
                                                    year: 'numeric'
                                                })}
                                            </div>
                                        </div>
                                        <div>
                                            <span style={{ color: '#757575', fontSize: '0.9rem' }}>Flight Info</span>
                                            <div style={{ color: '#212121', fontWeight: '500' }}>
                                                {item.flight_number} at {item.flight_time}
                                            </div>
                                        </div>
                                        {item.is_round_trip && item.return_date && (
                                            <div>
                                                <span style={{ color: '#757575', fontSize: '0.9rem' }}>Return Date</span>
                                                <div style={{ color: '#212121', fontWeight: '500' }}>
                                                    {new Date(item.return_date).toLocaleDateString('en-US', {
                                                        day: 'numeric',
                                                        month: 'short',
                                                        year: 'numeric'
                                                    })}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    <div>
                                        <span style={{ color: '#757575', fontSize: '0.9rem' }}>Price</span>
                                        <div style={{ color: 'rgba(210, 110, 50, 0.95)', fontSize: '1.2rem', fontWeight: '600' }}>
                                            {item.price} {item.currency}
                                            <small style={{ color: '#666', fontSize: '0.8rem', marginLeft: '5px' }}>
                                                ({item.is_round_trip ? 'Round Trip' : 'One Way'})
                                            </small>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Transfer Type Info */}
                            {item.is_round_trip && (
                                <div className="info-group" style={{
                                    background: 'linear-gradient(135deg, rgba(255, 245, 235, 0.7) 0%, rgba(255, 235, 215, 0.5) 100%)',
                                    padding: '12px',
                                    borderRadius: '8px',
                                    marginBottom: '10px',
                                    border: '1px solid rgba(255, 200, 150, 0.3)'
                                }}>
                                    <div className="d-flex align-items-center gap-2">
                                        <i className="isax isax-arrow-swap-horizontal" style={{ color: 'rgba(220, 120, 60, 0.8)', fontSize: '16px' }}></i>
                                        <span style={{ 
                                            color: 'rgba(210, 110, 50, 0.9)',
                                            fontSize: '0.9rem',
                                            fontWeight: '600'
                                        }}>Round Trip Transfer</span>
                                        <span style={{ color: '#666', fontSize: '0.85rem' }}>
                                            - Includes return journey
                                        </span>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    // Render Hotel Item (existing)
    const renderHotelItem = (item, index) => (
        <div key={index} className="card cart-item" style={{
            borderRadius: '15px',
            border: 'none',
            boxShadow: '0 0 20px rgba(0,0,0,0.05)',
            marginBottom: '20px',
            transition: 'transform 0.3s ease',
            position: 'relative'
        }}>
            <button 
                onClick={() => handleRemoveItem(item.id)}
                className="remove-item-btn"
                style={{
                    position: 'absolute',
                    top: '15px',
                    right: '15px',
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    background: 'rgba(220, 53, 69, 0.1)',
                    border: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    zIndex: 1,
                    opacity: removeLoading ? 0.5 : 1
                }}
                disabled={removeLoading}
            >
                {removeLoading ? (
                    <div className="spinner-border spinner-border-sm text-danger" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                ) : (
                    <FaTrash style={{ 
                        color: '#dc3545',
                        fontSize: '14px',
                        transition: 'all 0.3s ease'
                    }} />
                )}
            </button>
            <div className="card-body">
                <div className="row">
                    <div className="col-md-4">
                        <div className="cart-item-image" style={{
                            height: '200px',
                            overflow: 'hidden',
                            borderRadius: '10px',
                            marginBottom: '10px'
                        }}>
                            <img 
                                src={item.hotel.image || "https://via.placeholder.com/400x200"} 
                                alt={item.hotel.name} 
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover'
                                }}
                                onContextMenu={(e) => e.preventDefault()}
                                onDragStart={(e) => e.preventDefault()}
                            />
                        </div>
                        {/* Cancellation Policy Button */}
                        {item.room.rates[0].cancellationPolicies && item.room.rates[0].cancellationPolicies.length > 0 && (
                            <button 
                                className="cancellation-button"
                                onClick={() => setSelectedItem(item)}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    padding: '6px 10px',
                                    border: '1px solid #e9ecef',
                                    borderRadius: '8px',
                                    background: 'white',
                                    width: '100%',
                                    textAlign: 'left',
                                    transition: 'all 0.3s ease',
                                    fontSize: '0.75rem',
                                    marginBottom: '10px'
                                }}
                            >
                                <FaInfoCircle style={{ color: '#1e88e5', fontSize: '14px' }} />
                                <span>View Cancellation Policy</span>
                            </button>
                        )}
                    </div>
                    <div className="col-md-8">
                        <div className="cart-item-details">
                            <h5 style={{ 
                                color: '#1e88e5',
                                fontSize: '1.5rem',
                                fontWeight: '600',
                                marginBottom: '10px'
                            }}>{item.hotel.name}</h5>
                            
                            <div className="info-group" style={{
                                background: '#f8f9fa',
                                padding: '12px',
                                borderRadius: '8px',
                                marginBottom: '10px'
                            }}>
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <span style={{ 
                                            color: '#666',
                                            fontSize: '0.9rem',
                                            marginBottom: '5px',
                                            display: 'block'
                                        }}>{item.room.name}</span>
                                        <span style={{ 
                                            color: '#1e88e5',
                                            fontSize: '0.9rem',
                                            fontWeight: '500'
                                        }}>Quantity: {item.quantity}</span>
                                    </div>
                                    <div>
                                        <span style={{ 
                                            color: '#1e88e5',
                                            fontSize: '0.9rem',
                                            fontWeight: '500',
                                            marginRight: '5px'
                                        }}>Board Type:</span>
                                        <span style={{ color: '#666' }}>{item.room.rates[0].boardName}</span>
                                    </div>
                                </div>
                            </div>

                            <div className="info-group" style={{
                                background: '#f8f9fa',
                                padding: '12px',
                                borderRadius: '8px',
                                marginBottom: '10px'
                            }}>
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <span style={{ 
                                            color: '#1e88e5',
                                            fontSize: '0.9rem',
                                            fontWeight: '500',
                                            marginRight: '5px'
                                        }}>Guests:</span>
                                        <span style={{ color: '#666' }}>
                                            {item.room.rates[0].adults} Adult{item.room.rates[0].adults > 1 ? 's' : ''}
                                            {item.room.rates[0].children > 0 ? `, ${item.room.rates[0].children} Child${item.room.rates[0].children > 1 ? 'ren' : ''}` : ''}
                                        </span>
                                    </div>
                                    <div>
                                        <span style={{ 
                                            color: '#1e88e5',
                                            fontSize: '0.9rem',
                                            fontWeight: '500',
                                            marginRight: '5px'
                                        }}>Nights:</span>
                                        <span style={{ color: '#666' }}>
                                            {item.nights} Night{item.nights > 1 ? 's' : ''}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <div className="info-group" style={{
                                background: '#f8f9fa',
                                padding: '12px',
                                borderRadius: '8px',
                                marginBottom: '10px'
                            }}>
                                <div className="d-flex justify-content-between align-items-center">
                                    <div className="d-flex align-items-center" style={{ gap: '20px' }}>
                                        <div>
                                            <span style={{ color: '#757575', fontSize: '0.9rem' }}>Check-in</span>
                                            <div style={{ color: '#212121', fontWeight: '500' }}>
                                                {new Date(item.check_in).toLocaleDateString('en-US', {
                                                    day: '2-digit',
                                                    month: 'long',
                                                    year: 'numeric'
                                                })}
                                            </div>
                                        </div>
                                        <div>
                                            <span style={{ color: '#757575', fontSize: '0.9rem' }}>Check-out</span>
                                            <div style={{ color: '#212121', fontWeight: '500' }}>
                                                {new Date(item.check_out).toLocaleDateString('en-US', {
                                                    day: '2-digit',
                                                    month: 'long',
                                                    year: 'numeric'
                                                })}
                                            </div>
                                        </div>
                                    </div>
                                    <div>
                                        <span style={{ color: '#757575', fontSize: '0.9rem' }}>Price</span>
                                        <div style={{ color: '#1e88e5', fontSize: '1.2rem', fontWeight: '600' }}>
                                            {item.price} {selectedCurrency}
                                            <small style={{ color: '#666', fontSize: '0.8rem', marginLeft: '5px' }}>
                                                ({item.quantity} {item.quantity === 1 ? 'room' : 'rooms'})
                                            </small>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    if (loading) {
        return (
            <Layout>
                <div className="content content-two">
                    <div className="container">
                        <div className="row justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
                            <div className="col-md-12 text-center">
                                <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <h5 className="mt-3" style={{ color: '#1e88e5' }}>Loading Cart...</h5>
                            </div>
                        </div>
                    </div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="breadcrumb-bar breadcrumb-bg-01 text-center">
                <div className="container">
                    <div className="row">
                        <div className="col-md-12 col-12">
                            <h2 className="breadcrumb-title mb-2">Shopping Cart</h2>
                            <nav aria-label="breadcrumb">
                                <ol className="breadcrumb justify-content-center mb-0">
                                    <li className="breadcrumb-item"><a href="/"><i className="isax isax-home5"></i></a></li>
                                    <li className="breadcrumb-item active" aria-current="page">Cart</li>
                                </ol>
                            </nav>
                        </div>
                    </div>
                </div>
            </div>

            <div className="content content-two">
                <div className="container">
                    {cart?.length === 0 ? (
                        <div className="row justify-content-center">
                            <div className="col-md-8 text-center">
                                <div className="empty-cart" style={{ padding: '40px 20px' }}>
                                    <FaShoppingCart style={{ fontSize: '64px', color: '#1e88e5', marginBottom: '20px' }} />
                                    <h3 style={{ color: '#1e88e5', marginBottom: '15px' }}>Your Cart is Empty</h3>
                                    <p style={{ color: '#666', marginBottom: '25px' }}>Looks like you haven't added any items to your cart yet.</p>
                                    <button 
                                        className="btn btn-primary"
                                        onClick={() => navigate('/')}
                                        style={{
                                            padding: '12px 30px',
                                            borderRadius: '30px',
                                            fontSize: '1.1rem',
                                            fontWeight: '500',
                                            background: 'linear-gradient(135deg, #1e88e5 0%, #1565c0 100%)',
                                            border: 'none',
                                            boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                                        }}
                                    >
                                        Homepage
                                    </button>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="row">
                            <div className="col-lg-8">
                                {cart?.map((item, index) => {
                                    // Render based on item type
                                    if (item.type === 'tour') {
                                        return renderTourItem(item, index);
                                    } else if (item.type === 'transfer') {
                                        return renderTransferItem(item, index);
                                    } else {
                                        // Default to hotel rendering for backward compatibility
                                        return renderHotelItem(item, index);
                                    }
                                })}
                            </div>
                            <div className="col-lg-4">
                                <div className="card cart-summary" style={{
                                    borderRadius: '15px',
                                    border: 'none',
                                    boxShadow: '0 0 20px rgba(0,0,0,0.05)',
                                    padding: '20px'
                                }}>
                                    <h5 style={{ 
                                        color: '#1e88e5',
                                        fontSize: '1.5rem',
                                        fontWeight: '600',
                                        marginBottom: '20px'
                                    }}>Cart Summary</h5>
                                    
                                    {/* Auto Clear Timer */}
                                    {isTimerActive && timeLeft > 0 && (
                                        <div className={`timer-container ${timeLeft <= 60 ? 'timer-warning' : ''}`} style={{
                                            background: timeLeft <= 60 
                                                ? 'linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)' 
                                                : 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)',
                                            padding: '12px 16px',
                                            borderRadius: '10px',
                                            marginBottom: '20px',
                                            border: timeLeft <= 60 ? '1px solid #f44336' : '1px solid #ffcc02',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                            animation: timeLeft <= 60 ? 'pulse 1s infinite' : 'none'
                                        }}>
                                            <FaClock style={{ 
                                                color: timeLeft <= 60 ? '#d32f2f' : '#f57c00', 
                                                fontSize: '16px' 
                                            }} />
                                            <div>
                                                <div style={{ 
                                                    fontSize: '0.8rem', 
                                                    color: timeLeft <= 60 ? '#b71c1c' : '#e65100',
                                                    fontWeight: '500',
                                                    marginBottom: '2px'
                                                }}>
                                                    {timeLeft <= 60 ? 'Cart clearing soon!' : 'Cart will be cleared in:'}
                                                </div>
                                                <div style={{ 
                                                    fontSize: '1.1rem', 
                                                    color: timeLeft <= 60 ? '#b71c1c' : '#bf360c',
                                                    fontWeight: '600',
                                                    fontFamily: 'monospace'
                                                }}>
                                                    {formatTime(timeLeft)}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <span style={{ color: '#757575', fontSize: '0.9rem' }}>Total Items</span>
                                        <span style={{ color: '#212121', fontWeight: '500' }}>
                                            {cart?.reduce((sum, item) => {
                                                return sum + (item.quantity || 1);
                                            }, 0)} Items
                                        </span>
                                    </div>

                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <span style={{ color: '#757575', fontSize: '0.9rem' }}>
                                            Services
                                        </span>
                                        <span style={{ color: '#212121', fontWeight: '500' }}>
                                            {(() => {
                                                const hotels = cart?.filter(item => item.type !== 'tour' && item.type !== 'transfer').length || 0;
                                                const tours = cart?.filter(item => item.type === 'tour').length || 0;
                                                const transfers = cart?.filter(item => item.type === 'transfer').length || 0;
                                                
                                                const services = [];
                                                if (hotels > 0) services.push(`${hotels} Hotel${hotels > 1 ? 's' : ''}`);
                                                if (tours > 0) services.push(`${tours} Tour${tours > 1 ? 's' : ''}`);
                                                if (transfers > 0) services.push(`${transfers} Transfer${transfers > 1 ? 's' : ''}`);
                                                
                                                return services.length > 0 ? services.join(', ') : 'No services';
                                            })()}
                                        </span>
                                    </div>

                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <span style={{ color: '#757575', fontSize: '0.9rem' }}>Total Price</span>
                                        <span style={{ color: '#1e88e5', fontSize: '1.2rem', fontWeight: '600' }}>
                                            {totalPrice} {selectedCurrency}
                                        </span>
                                    </div>

                                    <div className="d-flex flex-column gap-2">
                                        <button 
                                            className="btn btn-primary w-100"
                                            onClick={handleProceedToCheckout}
                                            style={{
                                                padding: '8px 20px',
                                                borderRadius: '20px',
                                                fontSize: '0.9rem',
                                                fontWeight: '500',
                                                background: 'linear-gradient(135deg, #1e88e5 0%, #1565c0 100%)',
                                                border: 'none',
                                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                            }}
                                        >
                                            Proceed to Checkout
                                        </button>
                                        <button 
                                            className="btn btn-danger w-100"
                                            onClick={handleClearCart}
                                            style={{
                                                padding: '8px 20px',
                                                borderRadius: '20px',
                                                fontSize: '0.9rem',
                                                fontWeight: '500',
                                                border: 'none',
                                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                            }}
                                        >
                                            <FaTrash style={{ marginRight: '5px' }} />
                                            Clear Cart
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Cancellation Policy Modal */}
            <Modal
                show={selectedItem !== null}
                onHide={() => setSelectedItem(null)}
                centered
                className="cancellation-modal"
            >
                <Modal.Header closeButton>
                    <h6 style={{
                        color: '#1e88e5',
                        fontSize: '1.2rem',
                        fontWeight: '500',
                        margin: '0'
                    }}>Cancellation Policy</h6>
                </Modal.Header>
                <Modal.Body className="p-4">
                    {selectedItem?.room.rates[0].cancellationPolicies?.map((policy, index) => (
                        <div key={index} className="cancellation-policy-container">
                            {/* Free Cancellation Badge */}
                            <div className="modern-badge free-cancellation">
                                <div className="badge-icon">
                                    <TiTick />
                                </div>
                                <div className="badge-content">
                                    <h6 className="badge-title">Free Cancellation Period</h6>
                                    <p className="badge-text">
                                        Cancel without any charges until {formatCancellationDate(policy.from)}
                                    </p>
                                </div>
                            </div>

                            {/* Partial Charge Badge */}
                            <div className="modern-badge partial-charge">
                                <div className="badge-icon">
                                    <FaInfoCircle />
                                </div>
                                <div className="badge-content">
                                    <h6 className="badge-title">Cancellation Fee Period</h6>
                                    <p className="badge-text">
                                        From {formatCancellationDate(policy.from)} to {formatCancellationDate(selectedItem.check_in)}<br/>
                                        <span className="fw-semibold">
                                            Fee: {cancellationPrices[selectedItem.id]?.[policy.from] || policy.amount} {selectedCurrency}
                                        </span>
                                    </p>
                                </div>
                            </div>

                            {/* Non-Refundable Badge */}
                            <div className="modern-badge non-refundable">
                                <div className="badge-icon">
                                    <ImCross />
                                </div>
                                <div className="badge-content">
                                    <h6 className="badge-title">Non-Refundable Period</h6>
                                    <p className="badge-text">
                                        After {formatCancellationDate(selectedItem.check_in)}<br/>
                                        <span className="fw-semibold">
                                            Full amount will be charged
                                        </span>
                                    </p>
                                </div>
                            </div>
                        </div>
                    ))}
                </Modal.Body>
            </Modal>

            <style jsx>{`
                .cancellation-modal .modal-content {
                    border-radius: 16px;
                    border: none;
                    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.1);
                }

                .cancellation-modal .modal-header {
                    border-bottom: 1px solid #e9ecef;
                    padding: 1.25rem;
                }

                .cancellation-modal .modal-body {
                    padding: 1.25rem;
                }

                .cancellation-policy-container {
                    display: flex;
                    flex-direction: column;
                    gap: 1rem;
                    margin-bottom: 1rem;
                }

                .modern-badge {
                    display: flex;
                    align-items: flex-start;
                    padding: 1.25rem;
                    border-radius: 12px;
                    transition: all 0.3s ease;
                }

                .modern-badge:hover {
                    transform: translateY(-2px);
                }

                .modern-badge.free-cancellation {
                    background: linear-gradient(135deg, #E8F5E9 0%, #C8E6C9 100%);
                    box-shadow: 0 2px 8px rgba(76, 175, 80, 0.15);
                }

                .modern-badge.partial-charge {
                    background: linear-gradient(135deg, #FFF3E0 0%, #FFE0B2 100%);
                    box-shadow: 0 2px 8px rgba(255, 152, 0, 0.15);
                }

                .modern-badge.non-refundable {
                    background: linear-gradient(135deg, #FFEBEE 0%, #FFCDD2 100%);
                    box-shadow: 0 2px 8px rgba(244, 67, 54, 0.15);
                }

                .badge-icon {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 36px;
                    height: 36px;
                    border-radius: 10px;
                    margin-right: 1rem;
                    flex-shrink: 0;
                }

                .free-cancellation .badge-icon {
                    background: #4CAF50;
                    color: white;
                }

                .partial-charge .badge-icon {
                    background: #FF9800;
                    color: white;
                }

                .non-refundable .badge-icon {
                    background: #F44336;
                    color: white;
                }

                .badge-icon svg {
                    font-size: 20px;
                }

                .badge-content {
                    flex: 1;
                }

                .badge-title {
                    font-size: 1rem;
                    font-weight: 600;
                    margin-bottom: 0.5rem;
                }

                .free-cancellation .badge-title {
                    color: #2E7D32;
                }

                .partial-charge .badge-title {
                    color: #E65100;
                }

                .non-refundable .badge-title {
                    color: #D32F2F;
                }

                .badge-text {
                    font-size: 0.875rem;
                    margin-bottom: 0;
                    line-height: 1.5;
                }

                .free-cancellation .badge-text {
                    color: #1B5E20;
                }

                .partial-charge .badge-text {
                    color: #F57C00;
                }

                .non-refundable .badge-text {
                    color: #B71C1C;
                }

                .fw-semibold {
                    font-weight: 600;
                }

                /* Timer Animation */
                @keyframes pulse {
                    0% {
                        transform: scale(1);
                        box-shadow: 0 0 0 0 rgba(244, 67, 54, 0.4);
                    }
                    50% {
                        transform: scale(1.02);
                        box-shadow: 0 0 0 8px rgba(244, 67, 54, 0.1);
                    }
                    100% {
                        transform: scale(1);
                        box-shadow: 0 0 0 0 rgba(244, 67, 54, 0);
                    }
                }

                .timer-warning {
                    animation: pulse 1s infinite;
                }

                @media (max-width: 768px) {
                    .modern-badge {
                        padding: 1rem;
                    }

                    .badge-icon {
                        width: 32px;
                        height: 32px;
                    }

                    .badge-title {
                        font-size: 0.9rem;
                    }

                    .badge-text {
                        font-size: 0.8rem;
                    }

                    .timer-container {
                        padding: 10px 12px !important;
                    }
                }
            `}</style>
        </Layout>
    );
} 